// notificationSound — the one-shot "tin" played when a new notification arrives.
//
// Synthesized with the Web Audio API rather than shipping an .mp3: no binary asset in
// the repo, nothing to fetch (so no CSP/asset-path concerns), and the pitch/decay can
// be tuned in code. Two detuned sine partials with an exponential decay read as a small
// struck bell; a single sine sounds like a test tone.
//
// ⚠️ Browser autoplay policy: audio is BLOCKED until the user has interacted with the
// page. That is a browser rule, not something code can bypass — so the very first
// notification after a fresh page load may be silent if the manager has not clicked
// anything yet. Every call is wrapped so a blocked or unsupported context can never
// throw into the caller; a missing sound must never break the notification itself.

/** Lazily created and reused — creating an AudioContext per sound leaks contexts. */
let audioContext: AudioContext | null = null;

function getContext(): AudioContext | null {
  try {
    const Ctor = window.AudioContext || (window as any).webkitAudioContext;
    if (!Ctor) return null;
    if (!audioContext) audioContext = new Ctor();
    return audioContext;
  } catch {
    return null;
  }
}

/**
 * Play a short bell "tin". Safe to call from anywhere: never throws, and silently
 * does nothing when audio is unavailable or blocked.
 */
export function playNotificationChime(): void {
  try {
    const ctx = getContext();
    if (!ctx) return;

    // Autoplay policy may leave the context suspended until a user gesture.
    if (ctx.state === 'suspended') void ctx.resume().catch(() => { /* stays silent */ });

    const now = ctx.currentTime;
    const master = ctx.createGain();
    master.gain.setValueAtTime(0.0001, now);
    master.gain.exponentialRampToValueAtTime(0.18, now + 0.01);   // fast strike
    master.gain.exponentialRampToValueAtTime(0.0001, now + 1.1);  // long decay
    master.connect(ctx.destination);

    // Fundamental plus a non-integer partial — the inharmonic ratio is what makes it
    // read as metal rather than as a flute.
    [
      { freq: 1568, gain: 1.0 },   // G6
      { freq: 2350, gain: 0.45 },  // ~1.5x, struck-metal shimmer
    ].forEach(({ freq, gain }) => {
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq, now);
      g.gain.setValueAtTime(gain, now);
      osc.connect(g);
      g.connect(master);
      osc.start(now);
      osc.stop(now + 1.2);
    });
  } catch {
    // Never let a missing sound break the notification it accompanies.
  }
}

export default playNotificationChime;
